/**
 * Стабильный бюджет: сам себе зарплата.
 *
 * Фрилансер, сдельщик, сезонный работник получают деньги рывками: то ничего
 * три недели, то сразу за два проекта. Планировать «до следующего прихода»
 * им нечем — прихода может не быть вовсе, — и любое приложение, построенное
 * на календаре зарплаты, у них врёт.
 *
 * Здесь предлагается другой ход: деньги, которые уже пришли, откладываются в
 * **буфер**, а из буфера человек платит себе по расписанию, которое выбрал
 * сам. Бюджет перестаёт зависеть от того, когда заплатит заказчик.
 *
 * Три правила, на которых всё держится, и ни одно не является формальностью:
 *
 * 1. **Выплата себе — не доход.** Деньги не появились: они перестали быть
 *    связанными. Посчитать их доходом значило бы показать человеку, что он
 *    зарабатывает вдвое больше, чем зарабатывает: один раз когда пришли, и
 *    второй когда он выплатил их себе. Поэтому выплата не создаёт
 *    `Tx.type = 'income'`, не входит в «Фактически пришло», не обучает ритм
 *    внешних доходов и не увеличивает капитал.
 * 2. **Одни и те же деньги не могут быть двумя запасами.** Буфер — отдельный
 *    резерв (`forKind: 'stable'`), и аварийная подушка на том же счёте
 *    остаётся своим резервом. Складывать их в одну сумму на счёте значило бы
 *    обещать человеку, что он защищён дважды.
 * 3. **Буфера не хватает — выплаты не было.** Приложение не выдумывает
 *    выплату из воздуха, не уменьшает её молча и не берёт денег из подушки.
 *    Оно предупреждает заранее и предлагает решения, а решает человек.
 *
 * Сколько лежит в буфере, знает резерв, а не поле плана: два изменяемых
 * остатка об одних и тех же рублях — это всегда расхождение в чью-то пользу.
 */
import { ZERO, add, clampPos, minM, share, sub, type Money } from './money.js';
import {
  addMonths, diffDays, isAfter, isBefore, plainDateFromLocal, type PlainDate,
} from './date.js';
import { isKnown, type Amount } from './amount.js';
import { occurrencesBetween, type PaymentSchedule } from './schedule.js';
import { paymentInstances } from './instances.js';
import type { Goal, Obligation, Reserve } from './types.js';

/** Как часто человек платит себе. Реже раза в месяц бюджетом не назовёшь. */
export type PayoutCadence = 'weekly' | 'biweekly' | 'monthly';

/** Сколько месяцев обязательного минимума должно лежать в буфере. */
export const MIN_MONTHS = 2;

/**
 * План стабильного бюджета.
 *
 * Суммы буфера здесь нет намеренно: она живёт в резерве. План описывает
 * договор человека с самим собой — откуда, сколько, как часто и с какого дня.
 */
export interface StableBudgetPlan {
  id: string;
  /** Счёт, на котором физически лежит буфер. */
  accountId: string;
  /**
   * Куда приходит выплата.
   *
   * Совпадает с `accountId` — деньги никуда не едут, освобождается часть
   * резерва. Отличается — это настоящий перевод, и его должен подтвердить
   * человек: приложение не двигает деньги в банке.
   */
  toAccountId: string;
  cadence: PayoutCadence;
  /** Сумма одной выплаты. */
  payout: Money;
  firstPayoutDate: PlainDate;
  status: 'active' | 'paused' | 'stopped';
  createdAt: string;
  /**
   * Отрезки, когда режим стоял на паузе.
   *
   * Наступления внутри них не создаются вовсе — и это не мелочь учёта.
   * Расписание считается от опорной даты, поэтому без отрезков месяц паузы
   * возвращал бы человека к четырём просроченным выплатам, которых он не
   * пропускал: он сам их и остановил. Открытый отрезок (`to` пуст) означает
   * «стоим сейчас»: новых наступлений нет до возобновления.
   */
  pauses?: { from: PlainDate; to?: PlainDate }[];
  /** Приостановка сохраняет историю: выплаты прошлого никуда не деваются. */
  pausedAt?: string;
  stoppedAt?: string;
}

/**
 * Решение человека по одному наступлению выплаты.
 *
 * Не «состоявшаяся выплата»: пропуск — тоже решение, и записать его надо
 * так же явно. Вывести пропуск из бездействия нельзя ни при каких условиях —
 * молчание значит «ещё не решил», и приложение обязано ждать, а не
 * договаривать за человека.
 *
 * Идентификатор устойчив — план и дата по расписанию, — потому что одно и то
 * же нажатие приходит дважды: две вкладки, повтор доставки, дрогнувший палец.
 * Совпадение суммы и даты ничего не доказывает, а ключ доказывает.
 */
export interface StablePayoutFact {
  /** `${planId}:${scheduledDate}` */
  id: string;
  planId: string;
  scheduledDate: PlainDate;
  /**
   * Что человек решил.
   *
   * `paid` — выплатил себе, `skipped` — в этот раз не берёт,
   * `reconciled` — закрыто сверкой после долгого отсутствия: человек
   * подтвердил, что деньги брал, но сколько именно в каждую дату, не знает
   * никто. Сумма у такого решения нулевая намеренно — выдуманное число в
   * месячном отчёте хуже отсутствующего, а изменение остатка запаса там
   * видно и так.
   *
   * Поле необязательно ради записей, сделанных до его появления: они все
   * были выплатами.
   */
  decision?: 'paid' | 'skipped' | 'reconciled';
  /** Фактически выплачено: человек мог выплатить меньше. У пропуска ноль. */
  amount: Money;
  /**
   * Как деньги дошли.
   *
   * `release` — освобождение части резерва на том же счёте: банковской
   * операции не было. `transfer` — настоящий перевод, подтверждённый
   * человеком, с обычной операцией типа `transfer`. `none` — денег не
   * двигали вовсе: это пропуск.
   */
  via: 'release' | 'transfer' | 'none';
  txId?: string;
  /** Раскладка, в которой выплата случилась: в её «не распределено» деньги. */
  allocationId?: string;
  /**
   * Насколько выплата изменила «Пока не распределено» в этой раскладке.
   *
   * Обычно это сумма выплаты. Поле отдельно от `amount`, потому что резерв
   * может лежать на неликивидном счёте: освобождение денег там само по себе
   * ещё не делает их доступными для текущих расходов. Старые записи без поля
   * сохраняют прежний смысл — их эффект равен `amount`.
   */
  unallocatedDelta?: Money;
  /**
   * День, когда деньги двигались на самом деле.
   *
   * Отдельно от `scheduledDate`, потому что это разные вещи: просроченная
   * августовская выплата, проведённая 21 сентября, — сентябрьский факт, а
   * августовская строка графика. Месяц как отчёт считает по факту, график
   * сверяется по плану.
   *
   * Необязательно ради записей, сделанных до появления поля: у них день
   * проведения берётся из `paidAt`.
   */
  paidOn?: PlainDate;
  paidAt: string;
}

/**
 * Движение буфера: сколько в нём прибыло или убыло и почему.
 *
 * Журнал, а не второй остаток. Правда о сумме буфера остаётся в резерве —
 * два изменяемых числа об одних и тех же рублях всегда расходятся. Но без
 * истории нельзя ответить на главный вопрос месяца: «я проедаю запас или
 * пополняю его». По остатку это не видно: месяц, в котором пришло 200 000 и
 * ушло 200 000, выглядит как месяц, в котором не было ничего.
 */
export interface BufferMove {
  id: string;
  planId: string;
  date: PlainDate;
  /** Плюс — в буфер, минус — из буфера. */
  amount: Money;
  /**
   * Что за движение.
   *
   * `withdrawal` — деньги взяты из запаса **вне графика**: человеку
   * понадобились раньше срока. Выплатой это не является и дату графика не
   * закрывает — иначе несколько нажатий подряд закрыли бы будущие месяцы.
   */
  kind: 'open' | 'top_up' | 'from_fund' | 'payout' | 'release' | 'reconcile' | 'withdrawal';
  /** Выплата, если движение — это она. */
  payoutId?: string;
  /**
   * Принятая раскладка, внутри которой изменилось назначение денег.
   * Движения до принятия версии в неё не попадают: их сумма уже находится в
   * исходном снимке этой версии.
   */
  allocationId?: string;
  /**
   * Точное изменение строки «Пока не распределено».
   *
   * Это не всегда `-amount`: перекладывание из подушки в стабильный запас
   * может вообще не менять общий резерв, а движение между ликвидным и
   * неликивидным счётом меняет ещё и доступные сегодня деньги.
   */
  unallocatedDelta?: Money;
  createdAt: string;
}

/**
 * Остаток буфера на начало указанного дня.
 *
 * Считается назад от сегодняшнего: текущая сумма минус всё, что случилось
 * с этого дня и позже. Так историю не приходится хранить снимками, а ответ
 * всё равно точен — движения записаны все.
 *
 * Нужна именно дата, а не месяц: отчёт за сентябрь обязан показывать
 * сентябрьский остаток на конец месяца, а не сегодняшний. Раньше в конец
 * любого месяца подставлялся текущий, и сентябрь после октябрьской выплаты
 * показывал октябрьское число.
 */
export function bufferAt(
  now: Money, moves: readonly BufferMove[], from: PlainDate,
): Money {
  const later = moves.filter((m) => !isBefore(m.date, from));
  return sub(now, add(...later.map((m) => m.amount)));
}

/** Действующий план: активный либо приостановленный, но не выключенный. */
export const activeStablePlan = (
  plans: readonly StableBudgetPlan[],
): StableBudgetPlan | null => plans.find((p) => p.status === 'active')
  ?? plans.find((p) => p.status === 'paused')
  ?? null;

/** Сколько лежит в буфере этого плана. Правда — в резервах. */
export const bufferOf = (reserves: readonly Reserve[], planId: string): Money => add(
  ...reserves.filter((r) => r.forKind === 'stable' && r.forId === planId).map((r) => r.amount),
);

/** Ключ наступления выплаты. */
export const payoutId = (planId: string, date: PlainDate): string => `${planId}:${date}`;

/** Когда деньги двигались. Старые записи отвечают временем записи. */
export const paidOnOf = (f: StablePayoutFact): PlainDate =>
  f.paidOn ?? plainDateFromLocal(new Date(f.paidAt));

/** Что человек решил по наступлению. Старые записи все были выплатами. */
export const decisionOf = (f: StablePayoutFact): 'paid' | 'skipped' | 'reconciled' =>
  f.decision ?? 'paid';

/** Стоит ли режим на паузе прямо сейчас. */
export const isPaused = (plan: StableBudgetPlan): boolean =>
  plan.status === 'paused' || (plan.pauses ?? []).some((p) => p.to === undefined);

/** Попадает ли дата в отрезок паузы: такие наступления не создаются. */
const inPause = (plan: StableBudgetPlan, date: PlainDate): boolean =>
  (plan.pauses ?? []).some(
    (p) => !isBefore(date, p.from) && (p.to === undefined || isBefore(date, p.to)),
  );

/**
 * Расписание выплат.
 *
 * Считается тем же движком, что и платежи, и по той же причине: наступление
 * номер k берётся от исходной даты, а не от предыдущего. Иначе выплата 31-го
 * числа, перенесённая февралём на 28-е, навсегда осталась бы 28-й — человек
 * выбрал одно, а получил другое.
 */
export function payoutSchedule(plan: StableBudgetPlan): PaymentSchedule {
  const every = (n: number, unit: 'week' | 'month'): PaymentSchedule => ({
    kind: 'every',
    n,
    unit,
    anchor: plan.firstPayoutDate,
    shiftRule: 'exact',
    from: plan.firstPayoutDate,
  });
  if (plan.cadence === 'weekly') return every(1, 'week');
  if (plan.cadence === 'biweekly') return every(2, 'week');
  return every(1, 'month');
}

/**
 * Наступления выплат в отрезке — без тех, что пришлись на паузу.
 *
 * Пауза вырезает даты, а не сдвигает их: человек, остановивший режим на
 * месяц, при возобновлении должен увидеть **ближайшую новую** дату, а не
 * четыре пропущенные, которых он не пропускал.
 */
export const payoutOccurrences = (
  plan: StableBudgetPlan, from: PlainDate, to: PlainDate,
): PlainDate[] => occurrencesBetween(payoutSchedule(plan), from, to)
  .filter((d) => !inPause(plan, d));

/** Как часто в месяц случается выплата — для перевода сумм в месячные. */
export const perMonth = (cadence: PayoutCadence): number =>
  cadence === 'weekly' ? 365 / 7 / 12 : cadence === 'biweekly' ? 365 / 14 / 12 : 1;

/** Название периодичности по-русски. */
export const cadenceTitle = (c: PayoutCadence): string =>
  c === 'weekly' ? 'раз в неделю' : c === 'biweekly' ? 'раз в две недели' : 'раз в месяц';

// ───────────────────────────────────────────────────── готовность к режиму

export interface ReadinessInput {
  today: PlainDate;
  obligations: readonly Obligation[];
  reserves: readonly Reserve[];
  /** Обычная жизнь в месяц. «Не знаю» меняет ответ, а не подставляет ноль. */
  needBaseline: Amount;
  /** Сколько человек готов положить в буфер (или уже положил). */
  have: Money;
}

export interface StableReadiness {
  /** Двухмесячный минимум: обязательные платежи плюс необходимая жизнь. */
  required: Money;
  have: Money;
  /** Чего не хватает. Ноль — хватает. */
  missing: Money;
  ready: boolean;
  /** Обязательные платежи, попавшие в эти два месяца. */
  obligations: Money;
  /** Необходимая жизнь за два месяца. */
  need: Money;
  /** Чего расчёт не знает. Пока список непуст, минимум неизвестен. */
  unknowns: string[];
}

/**
 * Хватает ли денег на два месяца.
 *
 * Два месяца — не красивое число, а нижняя граница смысла: буфер на один
 * месяц ничего не стабилизирует, потому что кончается ровно тогда, когда
 * кончился бы и обычный остаток.
 *
 * Обязательные платежи считаются по настоящему расписанию за ближайшие два
 * месяца, а не «месячной нагрузкой ×2»: годовая страховка, попавшая в этот
 * отрезок, обязана войти в минимум целиком.
 */
export function stableReadiness(input: ReadinessInput): StableReadiness {
  const unknowns: string[] = [];
  const to = addMonths(input.today, MIN_MONTHS);
  const obligations = add(...paymentInstances({
    obligations: [...input.obligations],
    reserves: [...input.reserves],
    today: input.today,
    from: input.today,
    to,
  }).filter((i) => !i.settled).map((i) => i.amount));

  if (!isKnown(input.needBaseline)) {
    unknowns.push('Обычные траты неизвестны — без них нельзя посчитать запас на два месяца');
  }
  const need = isKnown(input.needBaseline)
    ? (input.needBaseline.value * MIN_MONTHS as Money)
    : ZERO;

  const required = add(obligations, need);
  const missing = clampPos(sub(required, input.have));
  return {
    required,
    have: input.have,
    missing,
    // Неизвестный минимум готовностью не считается: сказать «хватает», не
    // зная, на что именно, — это обещание, а не расчёт.
    ready: unknowns.length === 0 && missing <= ZERO,
    obligations,
    need,
    unknowns,
  };
}

/**
 * Подходит ли человеку этот режим.
 *
 * Предложение — не то же, что доступность: режим может открыть кто угодно и
 * увидеть честную нехватку. А **сам** продукт заговаривает о нём только там,
 * где он правда решает задачу: доход рывками, твёрдого горизонта нет, и
 * денег хватает на два месяца.
 */
export function suggestStable(input: {
  readiness: StableReadiness;
  /** Есть ли источник без дат: «когда как». */
  hasIrregularIncome: boolean;
  /** Есть ли надёжный регулярный горизонт (зарплата по числам). */
  hasRegularHorizon: boolean;
  /** Уже включён — предлагать нечего. */
  hasPlan: boolean;
}): boolean {
  if (input.hasPlan) return false;
  if (!input.readiness.ready) return false;
  if (input.hasRegularHorizon) return false;
  return input.hasIrregularIncome;
}

// ────────────────────────────────────────────────────── сколько продержится

/**
 * Состояние одного наступления выплаты.
 *
 * Шесть состояний, а не «выплачено / не выплачено», потому что различать
 * надо ровно те случаи, в которых приложение обязано вести себя по-разному:
 *
 * · `upcoming` — впереди и обеспечена: до неё живём, она задаёт срок;
 * · `due_today` — сегодня: деньги не приблизились, их надо себе отдать;
 * · `overdue` — день прошёл, а решения нет. Перескакивать через неё нельзя:
 *   человек либо получил эти деньги, либо нет, и пока он не сказал что
 *   именно, следующая дата сроком быть не может;
 * · `completed` — выплачено;
 * · `skipped` — человек **сказал**, что в этот раз не берёт;
 * · `unfunded` — буфера не хватает. Не срок и не обещание.
 */
export type PayoutState =
  | 'upcoming' | 'due_today' | 'overdue' | 'completed' | 'skipped' | 'unfunded';

/** Одно наступление выплаты с ответом, что с ним. */
export interface PayoutInstance {
  id: string;
  scheduledDate: PlainDate;
  amount: Money;
  state: PayoutState;
  /** Денег в буфере хватает на эту выплату целиком. */
  funded: boolean;
  /** Сколько останется в буфере после неё. */
  bufferAfter: Money;
  /** Решение принято: выплачено или явно пропущено. */
  decided: boolean;
  /** Уже выплачено. Пропуск выплатой не считается. */
  paid: boolean;
}

export interface OutlookInput {
  today: PlainDate;
  plan: StableBudgetPlan;
  /** Сколько сейчас в буфере. */
  buffer: Money;
  /** Что уже выплачено: повторно эти наступления денег не просят. */
  payouts: readonly StablePayoutFact[];
  /** На сколько наступлений вперёд смотреть. */
  ahead?: number;
}

export type StableWarning = 'none' | 'two_left' | 'one_left' | 'next_unfunded';

/**
 * Пропуск в сверке: человека долго не было, и решений нет за целый отрезок.
 *
 * Одно нерешённое наступление — обычное дело: сегодня день выплаты, а
 * человек ещё не взял деньги. Два и больше означают, что приложение не
 * знает о его жизни за этот период вовсе, и спрашивать по одной выплате
 * («что было 1 октября? а 8-го? а 15-го?») бессмысленно и жестоко: у
 * недельного режима после трёх месяцев отсутствия таких вопросов тринадцать.
 *
 * Поэтому отрезок закрывается **одним решением** — и до него следующая
 * выплата сроком не становится, а приложение не предлагает выплатить сразу
 * несколько старых периодов.
 */
export interface StableGap {
  /** Первое нерешённое наступление. */
  from: PlainDate;
  /** Последнее нерешённое наступление, не позже сегодняшнего дня. */
  to: PlainDate;
  /** Сколько их. */
  count: number;
  /** Во сколько это обошлось бы по нынешней сумме выплаты. */
  amount: Money;
  /**
   * Все нерешённые даты отрезка — целиком, без обрезки списком показа.
   *
   * Показ ограничен двумя десятками строк, а закрывать сверка обязана
   * **все**: иначе после ответа «не выплачивал» у недельного режима
   * осталась бы половина отрезка, и вопрос вернулся бы на следующий день.
   */
  dates: PlainDate[];
}

/** С какого числа нерешённых прошлых наступлений это уже пропуск в сверке. */
export const GAP_MIN = 2;

export interface StableOutlook {
  buffer: Money;
  payout: Money;
  cadence: PayoutCadence;
  /** Ближайшие наступления: дата, сумма, обеспечена ли, остаток после. */
  schedule: PayoutInstance[];
  /** Сколько выплат обеспечено целиком. */
  payoutsLeft: number;
  /** Дата первой необеспеченной выплаты — день, когда буфер кончится. */
  exhaustsOn: PlainDate | null;
  /** Сколько дней до неё. `null` — за пределами показа. */
  daysLeft: number | null;
  warning: StableWarning;
  /** Ближайшая невыплаченная дата: сегодняшняя, просроченная или будущая. */
  due: PayoutInstance | null;
  /**
   * Нерешённый отрезок прошлого. `null` — разбирать нечего.
   *
   * Пока он есть, прогноз не показывается: чего стоит буфер, известно, а
   * что происходило всё это время — нет.
   */
  gap: StableGap | null;
  /** Одной фразой, одинаково на всех экранах. */
  why: string;
}

/** На сколько наступлений вперёд смотрим по умолчанию. */
const AHEAD = 24;

/**
 * Что будет с буфером, если внешних доходов не будет.
 *
 * Это и есть главный вопрос режима, и отвечать на него надо буквально в этих
 * словах: не «хватит на три месяца», а «хватит на три месяца, если новых
 * доходов не будет». Разница — между прогнозом и обещанием.
 */
export function stableOutlook(input: OutlookInput): StableOutlook {
  const { plan } = input;
  const paidIds = new Map(input.payouts.map((p) => [p.id, p]));

  /*
    Назад смотрим до самого начала плана, а не на два месяца.

    Обрыв по времени тихо съедал нерешённое: у недельного режима после трёх
    месяцев отсутствия старые наступления просто исчезали, и приложение
    показывало спокойный срок — то есть делало ровно то, что §16.5
    запрещает. Нерешённое не может пропадать от того, что прошло время; оно
    закрывается решением человека, и только им.
  */
  const to = addMonths(input.today, Math.ceil(AHEAD / perMonth(plan.cadence)) + 1);
  const dates = payoutOccurrences(plan, plan.firstPayoutDate, to);

  /*
    Сначала — сколько прошлого осталось без ответа: от этого зависит, можно
    ли вообще считать прогноз.
  */
  const undecidedPast = dates.filter(
    (d) => isBefore(d, input.today) && !paidIds.has(payoutId(plan.id, d)),
  );
  const gap: StableGap | null = undecidedPast.length >= GAP_MIN
    ? {
      from: undecidedPast[0]!,
      to: undecidedPast[undecidedPast.length - 1]!,
      count: undecidedPast.length,
      amount: share(plan.payout, undecidedPast.length),
      dates: undecidedPast,
    }
    : null;

  const schedule: PayoutInstance[] = [];
  let left = input.buffer;
  let exhaustsOn: PlainDate | null = null;
  let payoutsLeft = 0;

  for (const date of dates) {
    const decided = paidIds.get(payoutId(plan.id, date));
    if (decided) {
      // Закрытое сверкой — не выплата и не пропуск: деньги брали, но сколько
      // именно в эту дату, не знает никто. Для расчётов это решённое.
      const skipped = decisionOf(decided) !== 'paid';
      schedule.push({
        id: payoutId(plan.id, date),
        scheduledDate: date,
        amount: decided.amount,
        state: skipped ? 'skipped' : 'completed',
        funded: true,
        bufferAfter: left,
        decided: true,
        paid: !skipped,
      });
      continue;
    }
    /*
      Прошлое из прогноза исключается, когда его накопился отрезок.

      Прогноз отвечает на вопрос «на сколько хватит буфера дальше». Пока
      нерешённое одно, оно и правда впереди — деньги ещё не взяты. А
      тринадцать нерешённых недель — это не будущее, а неизвестное прошлое:
      вычесть их из буфера значило бы показать человеку, что он уже в
      минусе, ничего о его жизни не зная.
    */
    const counts = !gap || !isBefore(date, input.today);
    const funded = counts && left >= plan.payout && plan.payout > ZERO;
    if (funded) {
      left = sub(left, plan.payout);
      payoutsLeft += 1;
    } else if (counts && exhaustsOn === null) {
      exhaustsOn = date;
    }
    schedule.push({
      id: payoutId(plan.id, date),
      scheduledDate: date,
      amount: plan.payout,
      /*
        Необеспеченность важнее календаря: «сегодня выплата», которой нечем
        платить, — не выплата, и звать её так значит обещать деньги.
      */
      state: isBefore(date, input.today)
        ? 'overdue'
        : !funded ? 'unfunded'
          : date === input.today ? 'due_today' : 'upcoming',
      funded,
      bufferAfter: left,
      decided: false,
      paid: false,
    });
  }

  const due = schedule.find((s) => !s.decided) ?? null;
  /*
    При разрыве «следующая не обеспечена» — неправда: нерешённое прошлое из
    прогноза исключено, и о нём речь не идёт. Предупреждение тогда говорит
    только о будущем, а про сам разрыв человек читает отдельной строкой.
  */
  const warning: StableWarning = !gap && due && !due.funded
    ? 'next_unfunded'
    : payoutsLeft === 1 ? 'one_left'
      : payoutsLeft === 2 ? 'two_left'
        : 'none';

  return {
    buffer: input.buffer,
    payout: plan.payout,
    cadence: plan.cadence,
    schedule: schedule.slice(0, (input.ahead ?? AHEAD)),
    payoutsLeft,
    exhaustsOn,
    daysLeft: exhaustsOn ? Math.max(0, diffDays(input.today, exhaustsOn)) : null,
    warning,
    due,
    gap,
    why: gap ? gapWhy(gap) : outlookWhy(plan, payoutsLeft),
  };
}

/**
 * Прогноз словами.
 *
 * «Устойчиво» здесь не произносится ни при каких условиях: устойчивость
 * определяется внешними доходами, а их этот расчёт не видит. Он видит только
 * буфер — и говорит ровно то, что видит.
 */
function outlookWhy(plan: StableBudgetPlan, left: number): string {
  if (plan.payout <= ZERO) return 'Сумма выплаты не задана';
  if (left === 0) {
    return 'Буфера не хватает даже на одну выплату — она не состоится, пока не пополнишь';
  }
  const months = left / perMonth(plan.cadence);
  // Дата исчерпания здесь не называется: она и так стоит отдельной строкой,
  // а в тексте выглядела бы сырым «2027-01-03» — экраны форматируют даты
  // по-русски, ядро этого делать не должно.
  const human = months >= 1
    ? monthWord(Math.floor(months))
    : `${left} ${left === 1 ? 'выплату' : 'выплаты'}`;
  return `Хватит примерно на ${human}, если новых доходов не будет`;
}

/** Прогноза нет, пока неизвестно прошлое, — и это надо сказать словами. */
const gapWhy = (gap: StableGap): string =>
  `Режим не сверялся с ${gap.from}: ${gap.count} выплат(ы) без ответа. `
  + 'Пока не разберём их, срок следующей выплаты не считается';

const monthWord = (n: number): string => {
  const words = ['ноль месяцев', 'месяц', 'два месяца', 'три месяца', 'четыре месяца',
    'пять месяцев', 'шесть месяцев', 'семь месяцев', 'восемь месяцев', 'девять месяцев',
    'десять месяцев', 'одиннадцать месяцев', 'год'];
  return words[Math.min(n, 12)] ?? `${n} мес.`;
};

/**
 * Выплата, до которой нужно дотянуть деньгам.
 *
 * Контрольной точкой бюджета выплата быть вправе: деньги уже существуют и
 * лежат в резерве, ждать их не от кого. Но только **будущая обеспеченная**,
 * и только когда за спиной не осталось нерешённого.
 *
 * Сегодняшняя и просроченная выплаты срока не дают и **загораживают
 * следующую**. Это главное правило этого расчёта, и стоит оно дорого:
 * перескочив через непроведённую выплату, приложение сказало бы «живём до
 * первого ноября», хотя человек не взял себе деньги первого октября. Он
 * прожил бы месяц на сумму, которой у него на руках нет, а приложение всё
 * это время показывало бы спокойный срок.
 *
 * Поэтому дальше пускает только решение человека: выплатил — открылась
 * следующая; сказал «в этот раз пропускаю» — тоже открылась. Молчание
 * решением не считается никогда.
 *
 * Необеспеченная выплата точкой не становится тем более: планировать жизнь
 * до денег, которых в буфере нет, — самый дорогой вид оптимизма.
 */
export function horizonPayout(o: StableOutlook, today: PlainDate): PayoutInstance | null {
  void today;
  if (!o.due) return null;
  return o.due.state === 'upcoming' ? o.due : null;
}

// ───────────────────────────────────────────────── осторожная оценка суммы

/** Внешний приход: деньги, пришедшие извне. */
export interface ExternalIncome {
  date: PlainDate;
  amount: Money;
  /** Источник, если приход регулярный. Подарок и продажа источника не имеют. */
  streamId?: string;
  kind: 'regular' | 'oneoff' | 'refund';
}

/** Сколько приходов нужно, чтобы говорить об устойчивости. */
export const ENOUGH_ARRIVALS = 6;
/** И за сколько разных месяцев. Шесть приходов за один месяц — не история. */
export const ENOUGH_MONTHS = 3;

export interface Sustainable {
  /** Осторожная сумма одной выплаты. `null` — истории мало, обещать нечего. */
  payout: Money | null;
  /** На скольких приходах основано. */
  arrivals: number;
  months: number;
  why: string;
}

/**
 * Осторожная сумма выплаты по истории внешних доходов.
 *
 * Считается по **нижней** оценке месячного дохода (P25), а не по среднему:
 * средний месяц у сдельщика бывает раз в год, и бюджет, построенный на нём,
 * кончается в первый же слабый.
 *
 * В оценку идут только приходы, привязанные к источнику дохода. Подарок,
 * продажа вещи и возврат источниками не являются по построению модели — у
 * них нет следующего раза, и планировать по ним нельзя. Перевод между своими
 * счетами сюда не попадает вовсе: это не приход.
 *
 * Пока приходов мало, возвращается `null`, и это не ошибка расчёта, а его
 * ответ: назвать выплату устойчивой не на чем.
 */
export function sustainablePayout(
  arrivals: readonly ExternalIncome[], cadence: PayoutCadence,
): Sustainable {
  const real = arrivals.filter((a) => a.kind === 'regular' && a.streamId && a.amount > ZERO);
  const byMonth = new Map<string, Money>();
  for (const a of real) {
    const m = a.date.slice(0, 7);
    byMonth.set(m, add(byMonth.get(m) ?? ZERO, a.amount));
  }
  const months = [...byMonth.values()].sort((a, b) => a - b);

  if (real.length < ENOUGH_ARRIVALS || months.length < ENOUGH_MONTHS) {
    return {
      payout: null,
      arrivals: real.length,
      months: months.length,
      why: `Пока мало истории: ${real.length} прихода(ов) за ${months.length} мес. `
        + `Назвать выплату устойчивой можно после ${ENOUGH_ARRIVALS} приходов за ${ENOUGH_MONTHS} месяца`,
    };
  }

  // P25 месячного дохода: нижняя четверть, а не середина.
  const idx = Math.max(0, Math.floor((months.length - 1) * 0.25));
  const p25 = months[idx] ?? ZERO;
  const perPayout = (p25 / perMonth(cadence)) as Money;
  // Округление вниз до тысячи: осторожная сумма не должна выглядеть
  // посчитанной до копейки — это придаёт ей точность, которой в ней нет.
  const rounded = (Math.floor(perPayout / 100_000) * 100_000) as Money;

  return {
    payout: clampPos(rounded),
    arrivals: real.length,
    months: months.length,
    why: `Нижняя оценка месячного дохода по ${real.length} приходам за ${months.length} мес.`,
  };
}

// ───────────────────────────────────────────────── раскладка нового прихода

export interface ArrivalSplit {
  /** В буфер стабильного бюджета. */
  toBuffer: Money;
  /** На ближайшие обязательные платежи, которым не хватает денег. */
  toObligations: Money;
  /** Остальное — решение человека. */
  unallocated: Money;
}

/**
 * Что предложить сделать с пришедшими деньгами.
 *
 * Предложение, а не действие: до подтверждения человека в буфер не уходит
 * ничего. Сначала закрываются необеспеченные платежи ближайшего отрезка —
 * иначе буфер наполнялся бы за счёт просрочки, — потом буфер добирается до
 * двухмесячного минимума, остальное остаётся нераспределённым.
 */
export function splitArrival(input: {
  amount: Money;
  /** Сколько не хватает платежам ближайшего отрезка. */
  obligationsGap: Money;
  /** Сколько не хватает буферу до минимума. */
  bufferGap: Money;
}): ArrivalSplit {
  let left = clampPos(input.amount);
  const toObligations = minM(clampPos(input.obligationsGap), left);
  left = sub(left, toObligations);
  const toBuffer = minM(clampPos(input.bufferGap), left);
  left = sub(left, toBuffer);
  return { toBuffer, toObligations, unallocated: left };
}

// ──────────────────────────────────────────────────────── изменение режима

export interface ChangeOutcome {
  was: StableOutlook;
  will: StableOutlook;
  /** Платежи, которые окажутся без денег после изменения. */
  atRisk: { label: string; dueDate: PlainDate; amount: Money }[];
}

/**
 * Последствия изменения суммы или периодичности.
 *
 * Показываются **до** изменения и всегда парой «было → станет»: сумма
 * выплаты в отрыве от даты исчерпания буфера ничего человеку не говорит.
 */
export function previewChange(input: {
  today: PlainDate;
  plan: StableBudgetPlan;
  buffer: Money;
  payouts: readonly StablePayoutFact[];
  next: { payout?: Money; cadence?: PayoutCadence };
  obligations: readonly Obligation[];
  reserves: readonly Reserve[];
}): ChangeOutcome {
  const base = { today: input.today, buffer: input.buffer, payouts: input.payouts };
  const was = stableOutlook({ ...base, plan: input.plan });
  const changed: StableBudgetPlan = {
    ...input.plan,
    ...(input.next.payout !== undefined ? { payout: input.next.payout } : {}),
    ...(input.next.cadence !== undefined ? { cadence: input.next.cadence } : {}),
  };
  const will = stableOutlook({ ...base, plan: changed });

  // Под угрозой — платежи после дня, когда буфер кончится: до него деньги
  // есть, после него их нет, и это надо назвать поимённо.
  const atRisk: ChangeOutcome['atRisk'] = [];
  if (will.exhaustsOn) {
    for (const inst of paymentInstances({
      obligations: [...input.obligations],
      reserves: [...input.reserves],
      today: input.today,
      from: will.exhaustsOn,
      to: addMonths(will.exhaustsOn, 2),
    })) {
      if (inst.settled || inst.remaining <= ZERO) continue;
      atRisk.push({ label: inst.label, dueDate: inst.scheduledDate, amount: inst.remaining });
    }
  }
  return { was, will, atRisk };
}

/**
 * Аварийная подушка: сколько в ней есть и можно ли ею закрыть выплату.
 *
 * Отдельной функцией, потому что ответ здесь всегда один и тот же —
 * «можно, но только с твоего отдельного подтверждения». Молча взять из
 * подушки нельзя ни при каких обстоятельствах: она собрана под другое.
 */
export const emergencyFund = (goals: readonly Goal[]): Goal | null =>
  goals.find((g) => g.kind === 'emergency_fund' && g.status === 'active') ?? null;

/**
 * Не записаны ли одни деньги в два запаса.
 *
 * Проверка по счёту: сумма резервов буфера и подушки не может быть больше
 * остатка счёта, на котором они лежат. Разделять их **обязательно** —
 * человек, у которого буфер и подушка на одной карте, иначе увидит, что
 * защищён дважды одними и теми же рублями.
 */
export function reservesOverlap(input: {
  accountId: string;
  balance: Money;
  reserves: readonly Reserve[];
}): { total: Money; over: Money; ok: boolean } {
  const total = add(
    ...input.reserves.filter((r) => r.accountId === input.accountId).map((r) => r.amount),
  );
  const over = clampPos(sub(total, input.balance));
  return { total, over, ok: over <= ZERO };
}

/** Что человек хочет сделать с наступлением. */
export type PayoutAction = 'pay' | 'skip';

/**
 * Можно ли принять это решение по этому наступлению.
 *
 * Одно правило на всех: экран рисует кнопку по нему же, по нему же
 * отказывает хранилище. Разойтись им нельзя — иначе кнопка обещает то, чего
 * запись не делает, или, того хуже, через хранилище проходит то, чего
 * человеку на экране не предлагали.
 */
export function decisionAllowed(input: {
  plan: StableBudgetPlan;
  outlook: StableOutlook;
  scheduledDate: PlainDate;
  action: PayoutAction;
}): { ok: boolean; reason?: string } {
  const { plan, outlook, scheduledDate, action } = input;
  if (plan.status !== 'active') {
    return { ok: false, reason: 'Режим приостановлен: решения по выплатам не принимаются' };
  }
  /*
    Пока прошлое не сверено, по одной выплате решать нечего: их там отрезок,
    и разбирать его надо целиком, а не тринадцатью вопросами подряд.
  */
  if (outlook.gap) return { ok: false, reason: 'Сначала сверимся за пропущенный период' };

  const due = outlook.due;
  if (!due) return { ok: false, reason: 'Выплат впереди не назначено' };
  if (due.scheduledDate !== scheduledDate) {
    return { ok: false, reason: 'Это не ближайшее наступление выплаты' };
  }
  if (action === 'pay' && !due.funded) {
    return { ok: false, reason: 'В запасе не хватает денег на эту выплату' };
  }
  /*
    Наступившую — да, будущую — нет. Ни для выплаты, ни для пропуска.

    С выплатой это стоило дороже всего: закрыв ближайшую дату, человек
    получал ближайшей следующую — тоже будущую и тоже обеспеченную, — и
    несколькими нажатиями подряд вычерпывал весь запас вперёд. Каждое
    нажатие при этом помечало **будущую** дату выплаченной, так что и
    месячный отчёт уезжал: деньги ушли в сентябре, а записаны октябрём.

    Деньги раньше срока человек взять вправе, но это другое действие —
    «Взять из запаса»: оно уменьшает запас и не притворяется исполнением
    графика.

    Пропуск будущего запрещён по своей причине: это ответ о случившемся, а
    не обещание. «Через неделю денег не возьму» человек передумает, а
    запись останется решением.
  */
  if (due.state === 'upcoming') {
    return {
      ok: false,
      reason: action === 'pay'
        ? 'Выплата ещё не наступила. Деньги раньше срока — через «Взять из запаса»'
        : 'Пропустить можно только наступившую выплату',
    };
  }
  return { ok: true };
}

/**
 * Ждёт ли наступление решения человека.
 *
 * Сегодняшнее и просроченное ждут: пока решения нет, следующая дата сроком
 * не становится. Пропуск при этом нельзя вывести из бездействия — только из
 * сказанного человеком.
 */
export const needsDecision = (due: PayoutInstance | null): boolean =>
  due !== null && (due.state === 'due_today' || due.state === 'overdue');
